/**
 * My Products hooks — TanStack Query wrappers around `HouseholdProductsApi`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateHouseholdProductRequest,
  LinkOfferToHouseholdProductRequest,
  UpdateHouseholdProductRequest,
} from '../../types/requests';
import type {
  BrandPreference,
  HouseholdProductPreferences,
} from '../../types/HouseholdProduct';
import type {
  SystemProductPreference,
  SystemProductSearchParams,
} from '../../types/SystemProduct';
import { useApi } from './context';
import { keys } from './keys';
import { makeOptimisticCacheUpdate } from './optimistic';
import { broadcastPreferenceChange } from '../preferenceBroadcast';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useHouseholdProducts() {
  const api = useApi();
  return useQuery({
    queryKey: keys.HouseholdProducts.all,
    queryFn: () => api.HouseholdProducts.fetchAll(),
  });
}

export function useSearchHouseholdProducts(query: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.HouseholdProducts.search(query),
    queryFn: () => api.HouseholdProducts.search(query),
    enabled: query.length > 0,
  });
}

export function useHouseholdProduct(productId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: productId ? keys.HouseholdProducts.one(productId) : ['household-products', 'disabled'],
    queryFn: () => api.HouseholdProducts.fetch(productId as string),
    enabled: Boolean(productId),
  });
}

export function useHouseholdProductOffers(productId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: productId
      ? keys.HouseholdProducts.offers(productId)
      : ['household-products', 'offers', 'disabled'],
    queryFn: () => api.HouseholdProducts.fetchLinkedOffers(productId as string),
    enabled: Boolean(productId),
  });
}

// T12.5 — the "Suggested · likely needed soon" shortlist for the Add-to-list
// sheet. Read-only display; the active list's items are passed as the exclude
// set so already-listed products never surface. `limit` defaults to 10 on the
// API. The query key splits on the excluded ids so adding/removing a list item
// refetches with the new exclusions.
export function useSuggestions(excludeHouseholdProductIds: string[] = []) {
  const api = useApi();
  return useQuery({
    queryKey: keys.suggestions.list(excludeHouseholdProductIds),
    queryFn: () =>
      api.HouseholdProducts.listSuggestions({ excludeHouseholdProductIds }),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateHouseholdProduct() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateHouseholdProductRequest) => api.HouseholdProducts.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
    },
  });
}

export function useUpdateHouseholdProduct() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; input: UpdateHouseholdProductRequest }) =>
      api.HouseholdProducts.update(args.productId, args.input),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.one(vars.productId) });
    },
  });
}

export function useDeleteHouseholdProduct() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => api.HouseholdProducts.delete(productId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
    },
  });
}

export function useLinkOfferToHouseholdProduct() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; input: LinkOfferToHouseholdProductRequest }) =>
      api.HouseholdProducts.linkOffer(args.productId, args.input),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.offers(vars.productId) });
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.one(vars.productId) });
    },
  });
}

export function useUpdateHouseholdProduct_RetailerOffer_Link() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      productId: string;
      offerId: string;
      input: Partial<Omit<LinkOfferToHouseholdProductRequest, 'retailerofferId'>>;
    }) => api.HouseholdProducts.updateOfferLink(args.productId, args.offerId, args.input),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.offers(vars.productId) });
    },
  });
}

export function useUnlinkOfferFromHouseholdProduct() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; offerId: string }) =>
      api.HouseholdProducts.unlinkOffer(args.productId, args.offerId),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.offers(vars.productId) });
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.one(vars.productId) });
    },
  });
}

// ---------------------------------------------------------------------------
// T12.1.7 — preferences surface (brands / retailers / store-locations) +
// cadence. Every write invalidates the product's preferences query so the
// Settings surface reflects the new state and persists across reload. All
// preferences are soft, advisory signals — never hard filters.
// ---------------------------------------------------------------------------

export function useHouseholdProductPreferences(productId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: productId
      ? keys.HouseholdProducts.preferences(productId)
      : ['household-products', 'preferences', 'disabled'],
    queryFn: () => api.HouseholdProducts.fetchPreferences(productId as string),
    enabled: Boolean(productId),
  });
}

export function useBrands() {
  const api = useApi();
  return useQuery({
    queryKey: keys.brands.all,
    queryFn: () => api.HouseholdProducts.fetchBrands(),
  });
}

function invalidatePreferences(
  qc: ReturnType<typeof useQueryClient>,
  productId: string
) {
  void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.preferences(productId) });
  // T12.5.1 (G) — a per-product brand/product preference change moves the
  // household catalog preference roll-up that feeds the Deals/Planning badges, so
  // the rolled-up read must refetch too (it is keyed at the household, not the
  // product). Without this, a preference change never refreshes the badges
  // same-tab.
  void qc.invalidateQueries({ queryKey: keys.households.catalogPreferenceSignals });
  // …and tell other tabs/windows to refresh their badge queries (G cross-tab).
  broadcastPreferenceChange();
}

// BUG21 — optimistic trio over the product's preferences cache. The product
// detail surface renders entirely from this query (no local mirror), so the
// in-place flip / remove / set controls reflect within ~1 frame. Add-from-search
// (the `useLink*` hooks) stays on the invalidate path — an optimistic insert
// needs display labels (brand name / store name / SystemProduct name) the link
// inputs don't carry. `extraKeys` covers reconciliation of derived reads (e.g.
// cadence also refreshes the product detail + list).
function optimisticPreferenceUpdate<TVars extends { productId: string }>(
  qc: ReturnType<typeof useQueryClient>,
  updater: (
    old: HouseholdProductPreferences | undefined,
    vars: TVars
  ) => HouseholdProductPreferences | undefined,
  extraKeys: (vars: TVars) => readonly unknown[][] = () => []
) {
  return {
    async onMutate(vars: TVars) {
      const queryKey = keys.HouseholdProducts.preferences(vars.productId);
      await qc.cancelQueries({ queryKey });
      const snapshot = qc.getQueryData<HouseholdProductPreferences>(queryKey);
      qc.setQueryData<HouseholdProductPreferences>(queryKey, (old) =>
        updater(old, vars)
      );
      return { snapshot };
    },
    onError(
      _error: unknown,
      vars: TVars,
      context: { snapshot: HouseholdProductPreferences | undefined } | undefined
    ) {
      if (context) {
        qc.setQueryData<HouseholdProductPreferences>(
          keys.HouseholdProducts.preferences(vars.productId),
          context.snapshot
        );
      }
    },
    onSettled(_data: unknown, _error: unknown, vars: TVars) {
      void qc.invalidateQueries({
        queryKey: keys.HouseholdProducts.preferences(vars.productId),
      });
      // T12.5.1 (G) — refresh the household catalog preference roll-up that feeds
      // the Deals/Planning badges (a brand/SystemProduct preference flip/remove
      // changes which badges fire). Cadence/store/retailer writes also pass
      // through here but don't move the roll-up — invalidating is harmless (the
      // read just refetches the same data).
      void qc.invalidateQueries({ queryKey: keys.households.catalogPreferenceSignals });
      broadcastPreferenceChange();
      for (const key of extraKeys(vars)) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
  };
}

export function useLinkBrandPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; brandId: string; preference: BrandPreference }) =>
      api.HouseholdProducts.linkBrandPreference(args.productId, args.brandId, args.preference),
    onSuccess: (_, vars) => invalidatePreferences(qc, vars.productId),
  });
}

export function useSetBrandPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; brandId: string; preference: BrandPreference }) =>
      api.HouseholdProducts.setBrandPreference(args.productId, args.brandId, args.preference),
    // BUG21 — flip the matching brand's role in place.
    ...optimisticPreferenceUpdate<{
      productId: string;
      brandId: string;
      preference: BrandPreference;
    }>(qc, (old, { brandId, preference }) => {
      if (!old) return old;
      return {
        ...old,
        brands: old.brands.map((b) =>
          b.brand_id === brandId ? { ...b, preference } : b
        ),
      };
    }),
  });
}

export function useUnlinkBrandPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; brandId: string }) =>
      api.HouseholdProducts.unlinkBrandPreference(args.productId, args.brandId),
    // BUG21 — remove the matching brand entry immediately.
    ...optimisticPreferenceUpdate<{ productId: string; brandId: string }>(
      qc,
      (old, { brandId }) => {
        if (!old) return old;
        return {
          ...old,
          brands: old.brands.filter((b) => b.brand_id !== brandId),
        };
      }
    ),
  });
}

export function useLinkRetailerPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; retailerId: string }) =>
      api.HouseholdProducts.linkRetailerPreference(args.productId, args.retailerId),
    onSuccess: (_, vars) => invalidatePreferences(qc, vars.productId),
  });
}

export function useUnlinkRetailerPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; retailerId: string }) =>
      api.HouseholdProducts.unlinkRetailerPreference(args.productId, args.retailerId),
    // BUG21 — remove the matching retailer entry immediately.
    ...optimisticPreferenceUpdate<{ productId: string; retailerId: string }>(
      qc,
      (old, { retailerId }) => {
        if (!old) return old;
        return {
          ...old,
          retailers: old.retailers.filter((r) => r.retailer_id !== retailerId),
        };
      }
    ),
  });
}

export function useLinkStoreLocationPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; storeLocationId: string }) =>
      api.HouseholdProducts.linkStoreLocationPreference(args.productId, args.storeLocationId),
    onSuccess: (_, vars) => invalidatePreferences(qc, vars.productId),
  });
}

export function useUnlinkStoreLocationPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; storeLocationId: string }) =>
      api.HouseholdProducts.unlinkStoreLocationPreference(args.productId, args.storeLocationId),
    // BUG21 — remove the matching store-location entry immediately.
    ...optimisticPreferenceUpdate<{ productId: string; storeLocationId: string }>(
      qc,
      (old, { storeLocationId }) => {
        if (!old) return old;
        return {
          ...old,
          storeLocations: old.storeLocations.filter(
            (s) => s.storelocation_id !== storeLocationId
          ),
        };
      }
    ),
  });
}

export function useSetHouseholdProductCadence() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; cadenceDays: number | null }) =>
      api.HouseholdProducts.setCadenceDays(args.productId, args.cadenceDays),
    // BUG21 — write the absolute cadence value into the preferences cache so the
    // cadence stepper reflects immediately. `onSettled` also reconciles the
    // product detail + list reads that derive from the new cadence.
    ...optimisticPreferenceUpdate<{ productId: string; cadenceDays: number | null }>(
      qc,
      (old, { cadenceDays }) => {
        if (!old) return old;
        return { ...old, cadencedays: cadenceDays };
      },
      (vars) => [
        [...keys.HouseholdProducts.one(vars.productId)],
        [...keys.HouseholdProducts.all],
      ]
    ),
  });
}

// ---------------------------------------------------------------------------
// T36.3 — measurement settings picker. List the valid choices for a product's
// linked SystemProducts; save a `user` override; or "use suggested" (clear). Each
// write invalidates the product reads so the rendered measurement refreshes.
// ---------------------------------------------------------------------------

export function useMeasurementChoices(productId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: productId
      ? keys.HouseholdProducts.measurementChoices(productId)
      : ['household-products', 'measurement-choices', 'disabled'],
    queryFn: () => api.HouseholdProducts.listMeasurementChoices(productId as string),
    enabled: Boolean(productId),
  });
}

export function useSetMeasurement() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; packingId: string }) =>
      api.HouseholdProducts.setMeasurement(args.productId, args.packingId),
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.one(vars.productId) });
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
    },
  });
}

// The "Show in" display-unit switch — keeps the packing, re-points the unit to a
// stored form. Same invalidation as the packing pick so the rendered measurement
// refreshes everywhere.
export function useSetDisplayUnit() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; unit: string }) =>
      api.HouseholdProducts.setDisplayUnit(args.productId, args.unit),
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.one(vars.productId) });
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
    },
  });
}

export function useUseSuggestedMeasurement() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string }) =>
      api.HouseholdProducts.useSuggestedMeasurement(args.productId),
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.one(vars.productId) });
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
    },
  });
}

// ---------------------------------------------------------------------------
// T12.1.8.2 — the SystemProduct surface: the search-first picker, the "who
// offers it" read, and the SystemProduct connection (the foreground
// product-preference control). Every connection write invalidates the
// product's preferences query so the pins list reflects the new state.
// ---------------------------------------------------------------------------

export function useSearchSystemProducts(params: SystemProductSearchParams, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: keys.SystemProducts.search(params),
    queryFn: () => api.HouseholdProducts.searchSystemProducts(params),
    enabled,
  });
}

export function useSystemProductOffers(systemProductId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: systemProductId
      ? keys.SystemProducts.offers(systemProductId)
      : ['system-products', 'offers', 'disabled'],
    queryFn: () => api.HouseholdProducts.listSystemProductOffers(systemProductId as string),
    enabled: Boolean(systemProductId),
  });
}

export function useLinkSystemProductPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      productId: string;
      systemProductId: string;
      preference: SystemProductPreference;
    }) =>
      api.HouseholdProducts.linkSystemProductPreference(
        args.productId,
        args.systemProductId,
        args.preference
      ),
    onSuccess: (_, vars) => invalidatePreferences(qc, vars.productId),
  });
}

export function useSetSystemProductPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      productId: string;
      systemProductId: string;
      preference: SystemProductPreference;
    }) =>
      api.HouseholdProducts.setSystemProductPreference(
        args.productId,
        args.systemProductId,
        args.preference
      ),
    // BUG21 — flip the matching SystemProduct connection's role in place.
    ...optimisticPreferenceUpdate<{
      productId: string;
      systemProductId: string;
      preference: SystemProductPreference;
    }>(qc, (old, { systemProductId, preference }) => {
      if (!old) return old;
      return {
        ...old,
        systemProducts: old.systemProducts.map((sp) =>
          sp.systemproduct_id === systemProductId ? { ...sp, preference } : sp
        ),
      };
    }),
  });
}

export function useUnlinkSystemProductPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { productId: string; systemProductId: string }) =>
      api.HouseholdProducts.unlinkSystemProductPreference(args.productId, args.systemProductId),
    // BUG21 — remove the matching SystemProduct connection immediately.
    ...optimisticPreferenceUpdate<{ productId: string; systemProductId: string }>(
      qc,
      (old, { systemProductId }) => {
        if (!old) return old;
        return {
          ...old,
          systemProducts: old.systemProducts.filter(
            (sp) => sp.systemproduct_id !== systemProductId
          ),
        };
      }
    ),
  });
}
